'use client'

import { createBrowserClient } from '@supabase/ssr'
import type { Database } from '@/types/supabase'
import { clearPasswordRecoveryIntent } from '@/lib/auth/passwordRecoveryClient'
import { loadBrowserSupabaseRuntimeConfig } from './browserRuntimeConfig'

type RawBrowserClient = ReturnType<typeof createBrowserClient<Database>>
type RawAuthClient = RawBrowserClient['auth']

export type BrowserAuthClient = {
  auth: Pick<
    RawAuthClient,
    | 'signInWithOAuth'
    | 'signInWithPassword'
    | 'signUp'
    | 'getSession'
    | 'getUser'
    | 'signOut'
    | 'resetPasswordForEmail'
    | 'updateUser'
    | 'onAuthStateChange'
    | 'refreshSession'
  >
}

/**
 * Browser access to Supabase is intentionally restricted to the Auth protocol.
 * Domain data belongs behind same-origin server routes.
 */
const AUTH_TIMEOUT_MS = 15_000
const AUTH_RETRY_COUNT = 3
const AUTH_RETRY_DELAY_MS = 1_000

let browserClientPromise: Promise<RawBrowserClient> | null = null

function withTimeout<T>(operation: Promise<T>, operationName: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`${operationName} timed out after ${AUTH_TIMEOUT_MS}ms`))
    }, AUTH_TIMEOUT_MS)

    operation.then(
      (value) => {
        clearTimeout(timeoutId)
        resolve(value)
      },
      (error) => {
        clearTimeout(timeoutId)
        reject(error)
      }
    )
  })
}

async function withRetry<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: Error | null = null
  for (let attempt = 0; attempt <= AUTH_RETRY_COUNT; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt === AUTH_RETRY_COUNT) break
      await new Promise((resolve) =>
        setTimeout(resolve, AUTH_RETRY_DELAY_MS * Math.pow(2, attempt))
      )
    }
  }
  throw lastError ?? new Error('Authentication operation failed.')
}

async function loadRawBrowserClient(): Promise<RawBrowserClient> {
  if (browserClientPromise) return browserClientPromise

  browserClientPromise = loadBrowserSupabaseRuntimeConfig()
    .then(({ key, url }) =>
      createBrowserClient<Database>(url, key, {
        auth: {
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          persistSession: true
        },
        global: {
          headers: { 'X-Client-Info': 'neuroplay-fantasy-football-browser-auth' }
        }
      })
    )
    .catch((error) => {
      browserClientPromise = null
      throw error
    })

  return browserClientPromise
}

export async function createBrowserAuthClient(): Promise<BrowserAuthClient> {
  const auth = (await loadRawBrowserClient()).auth

  return {
    auth: {
      signInWithOAuth: (credentials) =>
        withTimeout(auth.signInWithOAuth(credentials), 'OAuth sign in'),
      signInWithPassword: (credentials) =>
        withTimeout(auth.signInWithPassword(credentials), 'Password sign in'),
      signUp: (credentials) => withTimeout(auth.signUp(credentials), 'Sign up'),
      getSession: () =>
        withTimeout(
          withRetry(() => auth.getSession()),
          'Get session'
        ),
      getUser: (jwt) =>
        withTimeout(
          withRetry(() => auth.getUser(jwt)),
          'Get user'
        ),
      signOut: async (options) => {
        const fetcher = globalThis.fetch?.bind(globalThis)
        if (fetcher) await clearPasswordRecoveryIntent(fetcher)
        const result = await withTimeout(auth.signOut(options), 'Sign out')
        if (result.error) throw new Error('Sign out failed. Please try again.')
        return result
      },
      resetPasswordForEmail: (email, options) =>
        withTimeout(auth.resetPasswordForEmail(email, options), 'Reset password'),
      updateUser: (attributes, options) =>
        withTimeout(auth.updateUser(attributes, options), 'Update user'),
      onAuthStateChange: (callback) => auth.onAuthStateChange(callback),
      refreshSession: (session) => withTimeout(auth.refreshSession(session), 'Refresh session')
    }
  }
}
